import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AuthUser, MeResponse } from "../../shared/types";
import { api, ApiError } from "../lib/api";

type Status = "loading" | "authed" | "anon";

interface AuthValue {
  user: AuthUser | null;
  status: Status;
  /** What the Flashsuite menu needs; null until signed in, or when sign-in is not set up. */
  flashsuite: MeResponse["flashsuite"];
  /** Re-check the session with the server (`GET /auth/me`). */
  refresh: () => Promise<void>;
  /** End the session here and at Flashsuite. */
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

/**
 * Owns the signed-in user. On mount it asks the API who we are (`GET /auth/me`)
 * — a valid session cookie yields the user, otherwise we're anonymous and the
 * operator app sends the browser to sign in at Flashsuite.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [flashsuite, setFlashsuite] = useState<MeResponse["flashsuite"]>(null);
  const [status, setStatus] = useState<Status>("loading");

  const refresh = useCallback(async () => {
    try {
      const me = await api.me();
      setUser(me.user);
      setFlashsuite(me.flashsuite);
      setStatus("authed");
    } catch (err) {
      // The address names a workspace this account holds no seat in: the bare
      // address lands on the workspace the session is in.
      if (err instanceof ApiError && err.reason === "no_seat") {
        window.location.replace("/");
        return;
      }
      setUser(null);
      setStatus("anon");
    }
  }, []);

  // Why leave the page: Flashsuite's sign-in ends at its own address, which then sends the browser back.
  const logout = useCallback(async () => {
    const { to } = await api.logout().catch(() => ({ to: "/" }));
    window.location.assign(to);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<AuthValue>(
    () => ({ user, status, flashsuite, refresh, logout }),
    [user, status, flashsuite, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in user, or null when no provider wraps this tree (tests, public pages). */
export function useMaybeUser(): AuthUser | null {
  return useContext(AuthContext)?.user ?? null;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
